import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { AuthAPI } from "../api";
import {
  clearStoredAuth,
  getStoredAccessToken,
  getStoredUserSnapshot,
  hasStoredAccessToken,
  persistUserSnapshot,
} from "../utils/authStorage";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => getStoredUserSnapshot());
  const [isAuthenticated, setIsAuthenticated] = useState(() => hasStoredAccessToken());
  const [isValidating, setIsValidating] = useState(() => hasStoredAccessToken());

  const applyUser = useCallback((data) => {
    persistUserSnapshot(data);
    setUser(data);
    setIsAuthenticated(true);
  }, []);

  const validate = useCallback(async () => {
    const token = getStoredAccessToken();
    if (!token) {
      setUser(null);
      setIsAuthenticated(false);
      setIsValidating(false);
      return;
    }

    try {
      const res = await AuthAPI.get("auth/me/");
      applyUser(res.data);
    } catch (error) {
      // 401 + failed refresh: interceptor in api.js clears storage + hard-redirects to /login.
      // For transient errors (network, 5xx): leave state as-is; each page shows its own error.
      if (error.response?.status === 401) {
        clearStoredAuth();
        setUser(null);
        setIsAuthenticated(false);
      }
    } finally {
      setIsValidating(false);
    }
  }, [applyUser]);

  useEffect(() => {
    validate();
  }, [validate]);

  const completeLogin = useCallback((data) => {
    applyUser(data);
    setIsValidating(false);
  }, [applyUser]);

  const logout = useCallback(() => {
    clearStoredAuth();
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, isValidating, validate, completeLogin, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
